import { useState } from "react";
import { ArrowLeftRight, Download, FileSpreadsheet, FileText, History } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadUrl, formatDate, formatSize, previewUrl, versionDownloadUrl, versionPreviewUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const FileLinks = ({ entry, hrefFor, label }) => (
  <div className="flex flex-wrap gap-2">
    {["pdf", "excel"].map((fmt) => entry[fmt] && (
      <a key={fmt} href={hrefFor(fmt)} download data-testid={`version-download-${label}-${fmt}`}
        onClick={() => toast.success(`${fmt === "pdf" ? "PDF" : "Excel"} download started`, { description: label === "current" ? "Current revision" : `Revision ${entry.version || "—"}` })}
        className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
          fmt === "pdf" ? "bg-ph-lime text-ph-navy" : "bg-ph-violet/15 text-ph-ink hover:bg-ph-violet/25")}>
        {fmt === "pdf" ? <FileText className="h-3.5 w-3.5" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
        {fmt === "pdf" ? "PDF" : "Excel"} · {formatSize(entry[fmt].size)} <Download className="h-3 w-3" />
      </a>
    ))}
  </div>
);

const Pane = ({ title, sub, src, accent }) => (
  <div className="flex min-h-[320px] flex-1 flex-col overflow-hidden rounded-2xl border border-ph-line">
    <div className={cn("flex items-center justify-between px-4 py-2.5 text-xs font-semibold", accent)}>
      <span className="font-mono-ph uppercase tracking-wider">{title}</span><span className="font-mono-ph opacity-80">{sub}</span>
    </div>
    {src ? <iframe title={title} src={src} className="h-full w-full flex-1 bg-ph-raised" /> : <p className="m-auto p-6 text-center text-sm text-ph-muted">No PDF in this revision — download the Excel file instead.</p>}
  </div>
);

export function VersionsDialog({ doc, open, onOpenChange }) {
  const history = doc.history ?? [];
  const [compareIdx, setCompareIdx] = useState(null);
  const older = history.find((h) => h.index === compareIdx);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setCompareIdx(null); }}>
      <DialogContent data-testid="versions-dialog" data-lenis-prevent className={cn("flex max-h-[92vh] flex-col gap-5 overflow-y-auto rounded-3xl p-5 sm:p-7", older ? "h-[92vh] max-w-6xl" : "max-w-2xl")}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl font-extrabold text-ph-ink"><History className="h-5 w-5 text-ph-accent" /> Version history</DialogTitle>
          <DialogDescription>{doc.brand_name} — {doc.title}</DialogDescription>
        </DialogHeader>

        {older ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ph-muted">Comparing <strong>Rev {older.version || "—"}</strong> with the current <strong>Rev {doc.version || "—"}</strong></p>
              <button type="button" onClick={() => setCompareIdx(null)} data-testid="versions-back-btn" className="rounded-full border border-ph-line px-4 py-2 text-sm font-medium text-ph-ink hover:bg-ph-raised">Back to all versions</button>
            </div>
            <div className="flex flex-1 flex-col gap-4 md:flex-row">
              <Pane title={`Older · Rev ${older.version || "—"}`} sub={formatDate(older.updated_date)} src={older.pdf ? versionPreviewUrl(doc.id, older.index) : null} accent="bg-ph-raised text-ph-muted" />
              <Pane title={`Current · Rev ${doc.version || "—"}`} sub={formatDate(doc.updated_date)} src={doc.pdf ? previewUrl(doc.id) : null} accent="bg-ph-lime text-ph-navy" />
            </div>
          </>
        ) : (
          <ol className="relative ml-3 border-l-2 border-dashed border-ph-line">
            <li className="relative pb-7 pl-7" data-testid="version-current">
              <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full border-4 border-ph-surface bg-ph-lime shadow-[0_0_0_3px_rgba(182,255,92,0.3)]" />
              <p className="font-mono-ph text-[11px] uppercase tracking-[0.18em] text-ph-accent">Current</p>
              <p className="mt-1 font-display text-xl font-bold text-ph-ink">Rev {doc.version || "—"} <span className="ml-2 text-sm font-normal text-ph-muted">updated {formatDate(doc.updated_date)}</span></p>
              <div className="mt-3"><FileLinks entry={doc} label="current" hrefFor={(fmt) => downloadUrl(doc.id, fmt)} /></div>
            </li>
            {history.map((h) => (
              <li key={h.index} className="relative pb-7 pl-7 last:pb-0" data-testid={`version-${h.index}`}>
                <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-ph-line" />
                <p className="font-mono-ph text-[11px] uppercase tracking-[0.18em] text-ph-muted">Replaced {formatDate(h.archived_at)}</p>
                <p className="mt-1 font-display text-lg font-bold text-ph-ink/85">Rev {h.version || "—"} <span className="ml-2 text-sm font-normal text-ph-muted">updated {formatDate(h.updated_date)}</span></p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <FileLinks entry={h} label={`v${h.index}`} hrefFor={(fmt) => versionDownloadUrl(doc.id, h.index, fmt)} />
                  {(h.pdf || doc.pdf) && (
                    <button type="button" onClick={() => setCompareIdx(h.index)} data-testid={`version-compare-${h.index}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ph-violet/40 bg-ph-violet/10 px-3 py-1.5 text-xs font-semibold text-ph-ink hover:bg-ph-violet/20">
                      <ArrowLeftRight className="h-3.5 w-3.5" /> Compare with current
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
